import React from 'react';
import { Sparkles } from 'lucide-react';
import FabricGarmentInsights, { hasGarmentTypeInsights } from './FabricGarmentInsights';

/**
 * Read-only fabric analysis UI — matches the non-edit view in FabricCatalogueView.
 */
const FabricAnalysisReadOnly = ({ fabricAnalysis, fabricWeight }) => {
  if (!fabricAnalysis || typeof fabricAnalysis !== 'object') return null;

  const hasGarmentRec =
    hasGarmentTypeInsights(fabricAnalysis) ||
    (fabricAnalysis.women_garment_recommendations?.best_suited?.length > 0) ||
    (fabricAnalysis.men_garment_recommendations?.best_suited?.length > 0);

  const dg = fabricAnalysis.design_guidelines;
  const tips = dg?.print_or_cutting_tips;
  const hasPrintTips = Array.isArray(tips) ? tips.length > 0 : Boolean(tips && String(tips).trim());

  const hasDesign =
    dg &&
    (
      dg.ideal_garment_fit ||
      (dg.suggested_necklines?.length > 0) ||
      (dg.suggested_sleeves?.length > 0) ||
      (dg.ideal_length_options?.length > 0) ||
      hasPrintTips
    );

  const fa = fabricAnalysis.fabric_analysis || {};
  const hasFabricProps = Boolean(
    fa.fabric_type || fa.weave_or_knit || fa.fiber_guess || fa.opacity || fa.stretch || fa.gsm_weight || fabricWeight
  );

  const vc = fabricAnalysis.visual_characteristics;
  const hasVc = vc && (
    vc.visual_type ||
    (vc.primary_colors?.length > 0) ||
    (vc.secondary_colors?.length > 0)
  );

  const fp = fabricAnalysis.functional_properties;
  const hasFp = fp && (
    (fp.season_suitability?.length > 0) || (fp.climate_suitability?.length > 0)
  );

  if (!hasFabricProps && !hasVc && !hasFp && !hasGarmentRec && !hasDesign) return null;

  return (
    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl border border-indigo-200 p-3 sm:p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-indigo-600" />
        <p className="text-sm font-semibold text-indigo-700">Fabric Analysis</p>
      </div>

      {hasFabricProps && (
        <div className="bg-white/60 rounded-lg p-2.5 space-y-2">
          <p className="text-[11px] font-semibold text-gray-700">Fabric Properties</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            {fa.fabric_type && (
              <div>
                <span className="text-gray-600">Type:</span>
                <span className="ml-1 font-medium text-gray-800">{fa.fabric_type}</span>
              </div>
            )}
            {fa.weave_or_knit && (
              <div>
                <span className="text-gray-600">Weave/Knit:</span>
                <span className="ml-1 font-medium text-gray-800">{fa.weave_or_knit}</span>
              </div>
            )}
            {fa.fiber_guess && (
              <div>
                <span className="text-gray-600">Fiber:</span>
                <span className="ml-1 font-medium text-gray-800">{fa.fiber_guess}</span>
              </div>
            )}
            {fa.opacity && (
              <div>
                <span className="text-gray-600">Opacity:</span>
                <span className="ml-1 font-medium text-gray-800">{fa.opacity}</span>
              </div>
            )}
            {fa.stretch && (
              <div>
                <span className="text-gray-600">Stretch:</span>
                <span className="ml-1 font-medium text-gray-800">{fa.stretch}</span>
              </div>
            )}
            {(fa.gsm_weight || fabricWeight) && (
              <div>
                <span className="text-gray-600">GSM Weight:</span>
                <span className="ml-1 font-medium text-gray-800">{fa.gsm_weight || fabricWeight}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {hasVc && (
        <div className="bg-white/60 rounded-lg p-2.5 space-y-2">
          <p className="text-[11px] font-semibold text-gray-700">Visual</p>
          {vc.visual_type && (
            <div>
              <span className="text-xs text-gray-600">Fabric Visual: </span>
              <span className="text-xs font-medium text-gray-800">{vc.visual_type}</span>
            </div>
          )}
          {vc.primary_colors?.length > 0 && (
            <div>
              <span className="text-xs text-gray-600">Primary: </span>
              <span className="text-xs font-medium text-gray-800">{vc.primary_colors.join(', ')}</span>
            </div>
          )}
          {vc.secondary_colors?.length > 0 && (
            <div>
              <span className="text-xs text-gray-600">Secondary: </span>
              <span className="text-xs font-medium text-gray-800">{vc.secondary_colors.join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {hasFp && (
        <div className="bg-white/60 rounded-lg p-2.5 space-y-2">
          <p className="text-[11px] font-semibold text-gray-700">Functional</p>
          {fp.season_suitability?.length > 0 && (
            <div>
              <span className="text-xs text-gray-600">Season: </span>
              <span className="text-xs font-medium text-gray-800">{fp.season_suitability.join(', ')}</span>
            </div>
          )}
          {fp.climate_suitability?.length > 0 && (
            <div>
              <span className="text-xs text-gray-600">Climate: </span>
              <span className="text-xs font-medium text-gray-800">{fp.climate_suitability.join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {(hasGarmentRec || hasDesign) && (
        <details className="rounded-lg border border-indigo-200/70 bg-white/50">
          <summary className="cursor-pointer list-none px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold text-indigo-700">Recommendations & guidelines</span>
              <span className="text-[11px] font-medium text-slate-500">Collapsed by default</span>
            </div>
          </summary>
          <div className="px-3 pb-3">
            <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1 [scrollbar-width:thin]">
              {hasGarmentRec && (
                <div className="bg-white/60 rounded-lg p-2.5 space-y-2">
                  <p className="text-[11px] font-semibold text-gray-700">Garment Recommendations</p>
                  {hasGarmentTypeInsights(fabricAnalysis) ? (
                    <FabricGarmentInsights fabricAnalysis={fabricAnalysis} compact />
                  ) : (
                    <>
                      {fabricAnalysis.women_garment_recommendations?.best_suited?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-pink-600 mb-1">Women</p>
                          <div className="space-y-2">
                            {fabricAnalysis.women_garment_recommendations.best_suited.slice(0, 5).map((rec, idx) => (
                              <div key={idx} className="text-xs bg-pink-50 rounded p-2">
                                <p className="font-medium text-gray-800">{rec.garment}</p>
                                {rec.reason && <p className="text-gray-600 mt-0.5 line-clamp-2">{rec.reason}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {fabricAnalysis.men_garment_recommendations?.best_suited?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-blue-600 mb-1">Men</p>
                          <div className="space-y-2">
                            {fabricAnalysis.men_garment_recommendations.best_suited.slice(0, 5).map((rec, idx) => (
                              <div key={idx} className="text-xs bg-blue-50 rounded p-2">
                                <p className="font-medium text-gray-800">{rec.garment}</p>
                                {rec.reason && <p className="text-gray-600 mt-0.5 line-clamp-2">{rec.reason}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {hasDesign && (
                <div className="bg-white/60 rounded-lg p-2.5 space-y-2">
                  <p className="text-[11px] font-semibold text-gray-700">Design Guidelines</p>
                  {dg.ideal_garment_fit && (
                    <div>
                      <span className="text-xs text-gray-600">Ideal Fit: </span>
                      <span className="text-xs font-medium text-gray-800">{dg.ideal_garment_fit}</span>
                    </div>
                  )}
                  {dg.suggested_necklines?.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-600">Necklines: </span>
                      <span className="text-xs font-medium text-gray-800">{dg.suggested_necklines.join(', ')}</span>
                    </div>
                  )}
                  {dg.suggested_sleeves?.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-600">Sleeves: </span>
                      <span className="text-xs font-medium text-gray-800">{dg.suggested_sleeves.join(', ')}</span>
                    </div>
                  )}
                  {dg.ideal_length_options?.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-600">Length Options: </span>
                      <span className="text-xs font-medium text-gray-800">{dg.ideal_length_options.join(', ')}</span>
                    </div>
                  )}
                  {hasPrintTips && (
                    <div>
                      <span className="text-xs text-gray-600">Print/Cutting: </span>
                      <span className="text-xs font-medium text-gray-800">
                        {Array.isArray(tips) ? tips.join(', ') : String(tips)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </details>
      )}
    </div>
  );
};

export default FabricAnalysisReadOnly;
